<script lang="ts" setup>
  import dayjs from 'dayjs';
  import { computed, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useApi } from '@/composables/use-api.ts';
  import {
    AttendanceMonthDto,
    AttendanceMonthSummary,
  } from '@/services/api/attendance-month-api.ts';
  import { secondsToHoursAndMinutes } from '@/utils/item-printer.ts';
  import AttendanceProgressBar from '@/components/attendance/AttendanceProgressBar.vue';
  import { useAttendanceMonthStore } from '@/stores/attendance-month.ts';
  import { loadWithStatusAndErrorHandleAsync } from '@/services/loading-svc.ts';
  import { getAttendanceMissingAsync } from '@/services/attendance-svc.ts';
  import { AttendanceMissingDto } from '@/services/api/attendance-day-api.ts';
  import AttendanceCalendar from '@/components/date-time/AttendanceCalendar.vue';
  import {useAuthStore} from "@/stores/auth.ts";

  const router = useRouter();
  const { attendance } = useApi();
  const attendanceMonthStore = useAttendanceMonthStore();

	const auth = useAuthStore();
  const loading = ref<boolean>(false);
  const deadlining = ref<boolean>(false);
  const limit = ref<AttendanceMonthDto>();
  const summary = ref<AttendanceMonthSummary>();
  const deadline = ref<boolean>(false);
  const attendanceMissingItems = ref<AttendanceMissingDto[]>([]);
  const isModalDeadlineOn = ref(false);
  const currentYearMonth = ref<string>();
	const deadLineMonth = ref<string>();
	const deadLineCancelable = ref<boolean>(false);

  const hoursWorkedPercent = computed(() => {
    if (!summary.value || !limit.value) return 0;

    const a = summary.value.paidWorkTime / 3600;
    const b = a / limit.value.contractWorkHour;
    return Math.round(b * 100) || 0;
  });

  /** 연장 주간 근로시간 : 소정 주간 근로시간 + 법정 주간근로시간 */
  const extraDayWorkTime = computed(() => {
    return (
      (summary.value?.paidWorkContractExtraDayTime ?? 0) +
      (summary.value?.paidWorkStatutoryExtraDayTime ?? 0)
    );
  });

  /** 연장 야간 근로시간 : 소정 야간 근로시간 + 법정 야간 근로시간 */
  const extraNightWorkTime = computed(() => {
    return (
      (summary.value?.paidWorkContractExtraNightTime ?? 0) +
      (summary.value?.paidWorkStatutoryExtraNightTime ?? 0)
    );
  });

  async function closeAttendanceMonthDeadlineBySelf(missingCheck: boolean) {
    await loadWithStatusAndErrorHandleAsync(
      (status) => {
        deadlining.value = status;
      },
      async () => {
        if (!deadline.value || !currentYearMonth.value) return;

        if (
          !confirm(
            `${dayjs(currentYearMonth.value).format(
              'YYYY년 MM월'
            )} 근태 입력 마감을 하시겠습니까?\n최종 마감일인 (`+ deadLineMonth.value +`)월 5일까지 마감 취소가 가능합니다.`+
            `\n이 날짜가 지나면 근무 기록을 변경하실 수 없으니, 휴가 사용을 비롯한 개인` +
            `근태 기록이 정확하게 기록되었는지 다시 한번 확인해주시기 바랍니다.`
          )
        )
          return;
        if (missingCheck) {
          attendanceMissingItems.value = await getAttendanceMissingAsync(
            currentYearMonth.value
          );
          if (attendanceMissingItems.value.length > 0) {
            isModalDeadlineOn.value = true;
            return;
          }
        }
        isModalDeadlineOn.value = false;
        await attendanceMonthStore.deadlineAsync(currentYearMonth.value);
        await refreshData();
      }
    );
  }

async function cancelAttendanceMonthDeadlineBySelf() {
  await loadWithStatusAndErrorHandleAsync(
      (status) => {
        deadlining.value = status;
      },
      async () => {
        if (!deadLineCancelable.value || !currentYearMonth.value) return;

				
        if (
            !confirm(
                `최종 마감일 (` + deadLineMonth.value +`)월 5일까지 마감 취소가 가능합니다.\n${dayjs(currentYearMonth.value).format(
                    'YYYY년 MM월'
                )} 근태 입력 마감 취소를 하시겠습니까?`
            )
        )
          return;

        await attendance.month.deadlineCancelAsync(currentYearMonth.value);
        await refreshData();
      }
  );
}

  async function refreshData() {
    limit.value = undefined;
    summary.value = undefined;
    deadline.value = false;
    deadLineCancelable.value = false;

    await loadWithStatusAndErrorHandleAsync(
      (status) => {
        loading.value = status;
      },
      async () => {
        if (!currentYearMonth.value) return;
        limit.value = await attendance.month.getAsync(
          auth.user.employeeNo,
          currentYearMonth.value
        );
        if (!limit.value) {
          return;
        }

        summary.value = await attendance.month.getSummaryAsync(
          limit.value.ccmId
        );
        // 마감 버튼 활성 여부
        deadline.value = await attendance.month.getDeadlineAsync(
          currentYearMonth.value
        );
        // 마감 취소 버튼 활성 여부
        deadLineCancelable.value = await attendance.month.getCancelableDeadlineAsync(
            currentYearMonth.value
        );
      }
    );
  }

  function onChangeMonth(yearMonth: string) {
    currentYearMonth.value = yearMonth;
	  deadLineMonth.value = dayjs(yearMonth).add(1,'month').format('MM');
    refreshData();
  }
</script>

<template>
  <DefaultLayout title="근태 현황">
    <div class="mb-5">
      <AttendanceCalendar
        @on-change-month="onChangeMonth"
        @on-day-click="
          (selectDate:dayjs.Dayjs) =>
            router.push({
              name: 'AttendanceDay',
              params: {
                targetDate: selectDate.format('YYYY-MM-DD'),
              },
            })
        " />
    </div>
    <template v-if="deadLineCancelable">
    <button
      :disabled="!deadLineCancelable || deadlining"
      class="btn btn-block btn-primary"
      @click="cancelAttendanceMonthDeadlineBySelf()">
      <span
        v-if="deadlining"
        class="loading loading-spinner"/>
      근태 입력 마감 취소
    </button>
    </template>
    <template v-else>
    <button
      :disabled="!deadline || deadlining"
      class="btn btn-block btn-primary"
      @click="closeAttendanceMonthDeadlineBySelf(true)">
      <span
        v-if="deadlining"
        class="loading loading-spinner" />
      근태 입력 마감
    </button>
    </template>

    <div class="mt-4 mb-4 divider">🗓️</div>

    <skeleton
      v-if="loading"
      class="my-5" />
    <div v-else>
      <AttendanceProgressBar
        v-if="limit?.id && summary?.paidWorkTime"
        :attendance-month="limit"
        :vacation-time="summary?.vacationPayTime ?? 0"
        :work-time="summary?.paidWorkTime ?? 0"
        class="my-2" />
      <div class="pt-2 pb-6">
        <div class="flex px-2 text-sm">
          <div class="flex-auto">
            의무 근로 시간
          </div>
          <div class="text-right">{{ limit?.contractWorkHour ?? 0 }}시간</div>
        </div>

        <!-- <div class="m-1.5 divider divider-h" />

        <div class="flex px-2 text-sm">
          <div class="flex-auto">법정 근무 시간</div>
          <div class="text-right">{{ limit?.statutoryWorkHour ?? 0 }}시간</div>
        </div> -->

        <div class="m-1.5 divider divider-h" />

        <div class="flex px-2 text-sm">
          <div class="flex-auto">최대 근로 시간</div>
          <div class="text-right">
            {{
              (limit?.statutoryWorkHour ?? 0) + (limit?.maxExtraWorkHour ?? 0)
            }}시간
          </div>
        </div>

        <div class="m-1.5 divider divider-h" />

        <div class="flex px-2 text-sm">
          <!-- 누적 근로 시간 -->
          <div class="flex-auto">실제 근로 시간</div>
          <div class="text-right">
            <span class="text-secondary font-semibold">
              {{ secondsToHoursAndMinutes(summary?.paidWorkTime ?? 0) }}
            </span>
            /
            <span class="font-semibold">
              {{ limit?.contractWorkHour ?? 0 }}시간
            </span>
            <span class="ml-1 font-semibold badge badge-secondary text-white">
              {{ hoursWorkedPercent }}%
            </span>
          </div>
        </div>

        <div class="m-1.5 divider divider-h" />

        <div class="flex px-2 text-sm">
          <div class="flex-auto">휴가시간</div>
          <div class="text-right">
            {{ secondsToHoursAndMinutes(summary?.vacationPayTime ?? 0) }}
          </div>
        </div>

        <div class="m-1.5 divider divider-h" />

        <div class="flex px-2 text-sm">
          <div class="flex-auto">인정 근로 시간</div>
          <div class="text-right">
            {{
              secondsToHoursAndMinutes(
                (summary?.vacationPayTime ?? 0) + (summary?.paidWorkTime ?? 0)
              )
            }}
          </div>
        </div>

        <div class="m-1.5 divider divider-h" />

        <div class="flex px-2 text-sm">
          <div class="flex-auto">평일 야간 근로 시간</div>
          <div class="text-right">
            {{ secondsToHoursAndMinutes(summary?.paidWorkNightTime ?? 0) }}
          </div>
        </div>

        <div class="m-1.5 divider divider-h" />

        <div
          class="collapse"
          tabindex="0">
          <div class="flex px-2 text-sm">
            <div class="flex-auto">연장 근로 시간</div>
            <div class="text-right link">
              {{
                secondsToHoursAndMinutes(extraDayWorkTime + extraNightWorkTime)
              }}
            </div>
          </div>

          <div class="collapse-content pl-4 pr-2">
            <div class="my-5" />
            <div class="flex px-2 text-sm">
              <div class="flex-auto">연장 주간 근로 시간</div>
              <div class="text-right">
                {{ secondsToHoursAndMinutes(extraDayWorkTime) }}
              </div>
            </div>

            <div class="m-1.5 divider divider-h" />

            <div class="flex px-2 text-sm">
              <div class="flex-auto">연장 야간 근로 시간</div>
              <div class="text-right">
                {{ secondsToHoursAndMinutes(extraNightWorkTime) }}
              </div>
            </div>
          </div>
        </div>

        <div class="m-1.5 divider divider-h" />

        <div
          class="collapse"
          tabindex="1">
          <div class="flex px-2 text-sm">
            <div class="flex-auto">휴일 근로 시간</div>
            <div class="text-right link">
              {{ secondsToHoursAndMinutes(summary?.paidWorkHolidayTime ?? 0) }}
            </div>
          </div>
          <div class="collapse-content pl-4 pr-2">
            <div class="my-5" />
            <div class="flex text-sm">
              <div class="flex-auto">휴일 주간 근로 시간</div>
              <div class="text-right">
                {{
                  secondsToHoursAndMinutes(summary?.paidWorkHolidayDayTime ?? 0)
                }}
              </div>
            </div>
            <div class="my-1 divider divider-h" />
            <div class="flex text-sm">
              <div class="flex-auto">휴일 야간 근로 시간</div>
              <div class="text-right">
                {{
                  secondsToHoursAndMinutes(
                    summary?.paidWorkHolidayNightTime ?? 0
                  )
                }}
              </div>
            </div>
          </div>
        </div>

        <div class="m-1.5 divider divider-h" />

        <div class="flex px-2 text-sm">
          <div class="flex-auto font-semibold">FLEX 수당 기준 시간</div>
          <div class="text-right">
            <template
              v-if="limit?.deadlineDate && limit?.rewardTime != undefined">
              {{ secondsToHoursAndMinutes(limit?.rewardTime) }}
            </template>
            <template v-else>마감 이후 공개</template>
          </div>
        </div>
      </div>
    </div>
  </DefaultLayout>
  <AppModal
    id="confirm_deadline"
    has-close-x
    is-backdrop
    :is-visible="isModalDeadlineOn"
    @close="isModalDeadlineOn = false">
    <h3 class="text-lg font-bold">근태 누락</h3>
    <div class="mt-4 px-1 mx-auto">
      <span class="text-sm label-text">
        누락된 근무시작/종료, 야간/연장/휴일 근무 신청서 등이 있습니다.
      </span>
      <table class="table text-sm mt-2">
        <thead>
          <tr>
            <th class="py-2">누락일자</th>
            <th class="py-2">구분</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in attendanceMissingItems"
            :key="item.date">
            <td class="py-2">{{ item.date }}</td>
            <td class="py-2">{{ item.type }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="flex items-center justify-center mt-5 join">
      <button
        :disabled="deadlining"
        class="w-32 join-item btn"
        @click="isModalDeadlineOn = false">
        취소
      </button>
      <button
        :disabled="deadlining"
        class="w-32 join-item btn btn-error"
        @click="closeAttendanceMonthDeadlineBySelf(false)">
        <span
          v-if="deadlining"
          class="loading loading-spinner" />
        마감 하기
      </button>
    </div>
  </AppModal>
</template>

<style lang="scss" scoped>
  .divider-h:before {
    height: 0.05rem;
  }

  .divider-h:after {
    height: 0.05rem;
  }
</style>
